import React, { useState, useEffect, useRef } from 'react';

const WordCloudLoader = ({ onComplete, duration = 2000, isMorphing = false }) => {
  const [stage, setStage] = useState('loading');
  const [statusText, setStatusText] = useState('Initializing Feedback Analysis...');

  useEffect(() => {
    const statusSequence = [
      { time: duration * 0.15, text: 'Collecting customer feedback...' },        // ~15% through
      { time: duration * 0.30, text: 'Processing text sentiment...' },           // ~30% through  
      { time: duration * 0.50, text: 'Extracting key themes...' },               // ~50% through
      { time: duration * 0.70, text: 'Analyzing word frequency...' },            // ~70% through
      { time: duration * 0.85, text: 'Building insights cloud...' },             // ~85% through
      { time: duration, text: 'FEEDBACK ANALYSIS COMPLETE!' }                   // 100%
    ];

    const timers = statusSequence.map(({ time, text }) =>
      setTimeout(() => setStatusText(text), time)
    );

    // Success state - green glow for 1 second
    const successTimer = setTimeout(() => {
      setStage('success');
    }, duration);

    // Complete and fade out after 1 second of green glow
    const completeTimer = setTimeout(() => {
      setStage('complete');
      if (onComplete) onComplete();
    }, duration + 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(successTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, duration]);

  const styles = {
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '400px',
      width: '600px',
      maxWidth: '90vw',
      background: isMorphing 
        ? 'linear-gradient(135deg, rgba(248, 250, 252, 0.5), rgba(241, 245, 249, 0.6))'
        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.99))',
      borderRadius: isMorphing ? '50px' : '20px',
      boxShadow: isMorphing 
        ? '0 10px 25px rgba(0, 0, 0, 0.05)' 
        : '0 25px 50px rgba(0, 0, 0, 0.15)',
      border: isMorphing 
        ? '1px solid rgba(148, 163, 184, 0.1)' 
        : '1px solid rgba(148, 163, 184, 0.3)',
      opacity: stage === 'complete' ? 0 : 1,
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      backdropFilter: isMorphing ? 'blur(5px)' : 'blur(20px)',
      position: 'relative',
      transform: isMorphing ? 'scale(0.7)' : 'scale(1)'
    },
    wordCloudContainer: {
      position: 'relative',
      width: '180px',
      height: '120px',
      margin: '20px 0',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'scale(0.5) rotate(180deg)' : 'scale(1) rotate(0deg)',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    },
    word: {
      position: 'absolute',
      fontWeight: 'bold',
      // FIXED: Use complete shorthand animation instead of mixing with animationDelay
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite' : 'none',
      transition: 'all 0.3s ease-in-out',
      userSelect: 'none',
    },
    word1: {
      fontSize: '22px',
      color: stage === 'success' ? '#22c55e' : '#3b82f6',
      top: '15px',
      left: '20px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 0s' : 'none',
    },
    word2: {
      fontSize: '16px',
      color: stage === 'success' ? '#22c55e' : '#10b981',
      top: '45px',
      right: '25px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 0.3s' : 'none',
    },
    word3: {
      fontSize: '20px',
      color: stage === 'success' ? '#22c55e' : '#f59e0b',
      bottom: '35px',
      left: '15px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 0.6s' : 'none',
    },
    word4: {
      fontSize: '14px',
      color: stage === 'success' ? '#22c55e' : '#ef4444',
      top: '8px',
      right: '40px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 0.9s' : 'none',
    },
    word5: {
      fontSize: '18px',
      color: stage === 'success' ? '#22c55e' : '#8b5cf6',
      bottom: '8px',
      right: '20px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 1.2s' : 'none',
    },
    word6: {
      fontSize: '15px',
      color: stage === 'success' ? '#22c55e' : '#06b6d4',
      top: '35px',
      left: '45px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 1.5s' : 'none',
    },
    word7: {
      fontSize: '17px',
      color: stage === 'success' ? '#22c55e' : '#ec4899',
      bottom: '15px',
      left: '75px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 1.8s' : 'none',
    },
    word8: {
      fontSize: '13px',
      color: stage === 'success' ? '#22c55e' : '#84cc16',
      top: '25px',
      right: '5px',
      // FIXED: Include delay in complete shorthand
      animation: stage !== 'complete' ? 'wordAppear 3s ease-in-out infinite 2.1s' : 'none',
    },
    statusText: {
      marginTop: '30px',
      fontSize: '18px',
      color: stage === 'success' ? '#22c55e' : '#475569',
      fontWeight: '600',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      textShadow: stage === 'success' ? '0 0 10px #22c55e' : 'none',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      textAlign: 'center',
      maxWidth: '400px',
      padding: '0 20px',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'translateY(20px)' : 'translateY(0px)'
    },
    securityBadge: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: stage === 'success' 
        ? 'rgba(34, 197, 94, 0.15)'
        : 'rgba(59, 130, 246, 0.1)',
      color: stage === 'success' ? '#22c55e' : '#3b82f6',
      padding: '8px 16px',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: '600',
      border: stage === 'success'
        ? '1px solid rgba(34, 197, 94, 0.4)'
        : '1px solid rgba(59, 130, 246, 0.3)',
      boxShadow: stage === 'success'
        ? '0 0 15px rgba(34, 197, 94, 0.3)'
        : '0 0 10px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'translateX(-50%) scale(0.5)' : 'translateX(-50%) scale(1)'
    },
    cloudBackground: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      background: stage === 'success'
        ? 'radial-gradient(circle at center, rgba(34, 197, 94, 0.05), transparent)'
        : 'radial-gradient(circle at center, rgba(59, 130, 246, 0.05), transparent)',
      borderRadius: '50%',
      animation: stage !== 'complete' ? 'cloudPulse 4s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease-in-out',
    }
  };

  const keyframes = `
    @keyframes wordAppear {
      0%, 15% { 
        opacity: 0; 
        transform: scale(0.3) rotate(-10deg); 
      }
      20%, 80% { 
        opacity: 1; 
        transform: scale(1) rotate(0deg); 
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
      85%, 100% { 
        opacity: 0; 
        transform: scale(0.3) rotate(10deg); 
      }
    }
    
    @keyframes cloudPulse {
      0%, 100% { 
        transform: scale(1); 
        opacity: 0.3; 
      }
      50% { 
        transform: scale(1.1); 
        opacity: 0.6; 
      }
    }
    
    @keyframes morphToPage {
      0% {
        border-radius: 20px;
        transform: scale(1);
        opacity: 1;
      }
      50% {
        border-radius: 50px;
        transform: scale(0.8) rotate(180deg);
        opacity: 0.7;
      }
      100% {
        border-radius: 0px;
        transform: scale(3) rotate(360deg);
        opacity: 0;
      }
    }
  `;

  // Feedback words that appear in the cloud
  const feedbackWords = [
    { text: 'Excellent', id: 'word1' },
    { text: 'Quality', id: 'word2' },
    { text: 'Amazing', id: 'word3' },
    { text: 'Fast', id: 'word4' },
    { text: 'Helpful', id: 'word5' },
    { text: 'Friendly', id: 'word6' },
    { text: 'Perfect', id: 'word7' },
    { text: 'Great', id: 'word8' }
  ];

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.loadingContainer}>
        <div style={styles.securityBadge}>
          {stage === 'success' ? 'INSIGHTS READY' : 'PREPARING INSIGHTS'}
        </div>
        
        <div style={styles.wordCloudContainer}>
          <div style={styles.cloudBackground}></div>
          {feedbackWords.map((word, index) => (
            <div 
              key={word.id}
              style={{
                ...styles.word,
                ...styles[word.id]
              }}
            >
              {word.text}
            </div>
          ))}
        </div>
        
        <div style={styles.statusText}>
          {statusText}
        </div>
      </div>
    </>
  );
};

const SmartWordCloudLoader = ({ 
  isSmartLoading,
  loadingStage = 'scanning',
  dataProgress = {},
  onComplete = null
}) => {
  const [wordCloudPhase, setWordCloudPhase] = useState('initial');
  const [showSubtleProgress, setShowSubtleProgress] = useState(false);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const completedRef = useRef(false);
  
  // Cleanup function
  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  
  // Calculate actual data loading progress
  const completedSteps = Object.values(dataProgress).filter(Boolean).length;
  const totalSteps = Object.keys(dataProgress).length;
  const dataProgressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);
  
  // Safe completion function
  const safeComplete = () => {
    if (completedRef.current || !mountedRef.current) {
      return; // Already completed or unmounted
    }
    
    completedRef.current = true;
    console.log('WordCloud: Completing loading safely');
    setWordCloudPhase('complete');
    
    if (onComplete && mountedRef.current) {
      // Use requestAnimationFrame for smoother completion
      requestAnimationFrame(() => {
        if (mountedRef.current && onComplete) {
          setTimeout(onComplete, 800);
        }
      });
    }
  };
  
  // Main loading state logic
  useEffect(() => {
    // Don't proceed if already completed
    if (completedRef.current) return;
    
    cleanup(); // Clear any existing timeouts
    
    if (isSmartLoading) {
      // During smart loading, follow the smart loading stages
      setWordCloudPhase(loadingStage);
      setShowSubtleProgress(false);
    } else if (dataProgressPercent < 100) {
      // Smart loading done but data still loading - keep WordCloud spinning
      setWordCloudPhase('data-loading');
      setShowSubtleProgress(true);
      
      // Set a maximum timeout for data loading phase (10 seconds max)
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current && !completedRef.current) {
          console.warn('WordCloud: Data loading timeout reached, forcing completion');
          safeComplete();
        }
      }, 10000);
    } else {
      // Everything complete - trigger completion
      safeComplete();
    }
  }, [isSmartLoading, loadingStage, dataProgressPercent, onComplete]);
  
  // Additional safety check for edge cases
  useEffect(() => {
    if (completedRef.current) return;
    
    // If we've been in data-loading phase for too long, or if all critical data is ready
    if (wordCloudPhase === 'data-loading') {
      const criticalDataReady = dataProgress.hasSelectedStore && 
                               dataProgress.deckReady && 
                               dataProgress.chartReady && 
                               dataProgress.businessReady;
      
      // More aggressive completion - if we have 80%+ progress, consider it done
      const hasGoodProgress = dataProgressPercent >= 80;
      
      // Special case: if we don't have a selected store but other data is ready,
      // and we're in business view, we can still complete
      const businessViewReady = !dataProgress.hasSelectedStore && 
                               dataProgress.businessReady && 
                               dataProgress.storesReady;
      
      // If no stores are available at all, we should still complete
      const noStoresAvailable = dataProgress.storesReady && 
                               !dataProgress.hasSelectedStore && 
                               dataProgress.hasBusinesses;
      
      if (criticalDataReady || hasGoodProgress || businessViewReady || noStoresAvailable) {
        console.log('WordCloud: Completing loading via edge case detection', {
          criticalDataReady,
          hasGoodProgress,
          businessViewReady,
          noStoresAvailable,
          dataProgressPercent,
          dataProgress
        });
        safeComplete();
      }
    }
  }, [wordCloudPhase, dataProgress, dataProgressPercent]);
  
  // Emergency timeout - absolute maximum loading time
  useEffect(() => {
    const emergencyTimeout = setTimeout(() => {
      if (mountedRef.current && !completedRef.current) {
        console.error('WordCloud: Emergency timeout reached - forcing completion');
        safeComplete();
      }
    }, 20000); // 20 second absolute maximum
    
    return () => clearTimeout(emergencyTimeout);
  }, []);
  
  // Get WordCloud configuration based on current phase
  const getWordCloudConfig = () => {
    switch (wordCloudPhase) {
      case 'scanning':
        return { 
          duration: 4000, 
          isMorphing: false,
          message: "Finding your best sites..."
        };
      case 'loading':
        return { 
          duration: 3000, 
          isMorphing: false,
          message: "Loading dashboard..."
        };
      case 'optimizing':
        return { 
          duration: 2000, 
          isMorphing: false,
          message: "Almost ready..."
        };
      case 'ready':
        return { 
          duration: 1500, 
          isMorphing: false,
          message: "Preparing dashboard..."
        };
      case 'data-loading':
        return { 
          duration: 6000, 
          isMorphing: false,
          message: "Loading dashboard data..."
        };
      case 'complete':
        return { 
          duration: 1000, 
          isMorphing: true,
          message: "Ready!"
        };
      default:
        return { 
          duration: 3000, 
          isMorphing: false,
          message: "Loading..."
        };
    }
  };

  const config = getWordCloudConfig();

  // Don't render if completed
  if (completedRef.current && wordCloudPhase === 'complete') {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: wordCloudPhase === 'complete' 
        ? 'rgba(255, 255, 255, 0.3)' 
        : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: wordCloudPhase === 'complete' ? 'blur(5px)' : 'blur(10px)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
      opacity: wordCloudPhase === 'complete' ? 0 : 1,
      transform: wordCloudPhase === 'complete' ? 'scale(0.8)' : 'scale(1)',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      pointerEvents: wordCloudPhase === 'complete' ? 'none' : 'auto'
    }}>
      {/* Main WordCloud Loader */}
      <div style={{
        transform: wordCloudPhase === 'complete' ? 'scale(0.3) rotate(360deg)' : 'scale(1) rotate(0deg)',
        opacity: wordCloudPhase === 'complete' ? 0 : 1,
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}>
        <WordCloudLoader 
          onComplete={onComplete}
          duration={config.duration}
          isMorphing={config.isMorphing}
        />
      </div>
    </div>
  );
};


export default SmartWordCloudLoader;